import React, { useCallback, useRef, useState } from 'react';
import {
  View,
  FlatList,
  type ImageSourcePropType,
  ImageBackground,
  TouchableOpacity,
  type ListRenderItemInfo,
  type ViewabilityConfigCallbackPairs,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { twMerge } from 'tailwind-merge';

import ChevronLeftSVG from '~/credsystem/assets/svgs/chevron-left.svg';
import ChevronRightSVG from '~/credsystem/assets/svgs/chevron-right.svg';
import colors from '~/assets/themes/colors';
import { isWeb } from '~/utils/device';
import { useFocusEffect } from '@react-navigation/native';

interface ImgProps {
  src: {
    mobile: ImageSourcePropType | string;
    web: ImageSourcePropType | string;
  };
  chevronRigthColor?: string;
  onPress?: () => void;
}

interface ImageCarouselProps {
  images: ImgProps[];
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => {
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [bannerWidth, setBannerWidth] = useState<number>(0);

  const flatListRef = useRef<FlatList>(null);

  const viewabilityConfigCallbackPairs = useRef<ViewabilityConfigCallbackPairs>(
    [
      {
        viewabilityConfig: {
          waitForInteraction: false,
          viewAreaCoveragePercentThreshold: 100,
        },
        onViewableItemsChanged: ({ viewableItems }) => {
          if (viewableItems[0]?.index !== undefined) {
            setActiveIndex(viewableItems[0]?.index ?? 0);
          }
        },
      },
    ]
  );

  const scrollToIndex = (index: number) => {
    flatListRef.current?.scrollToIndex({
      index,
      animated: true,
    });
  };

  const Chevrons = useCallback(() => {
    if (!isWeb) return null;

    const currentImg = images[activeIndex];

    return (
      <View className="flex-row items-center w-full justify-between">
        {activeIndex !== 0 ? (
          <TouchableOpacity
            activeOpacity={1}
            onPress={() => {
              scrollToIndex(activeIndex - 1);
            }}
          >
            <ChevronLeftSVG color={colors.light[100]} width={15} height={31} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity disabled />
        )}

        {activeIndex !== images.length - 1 && (
          <TouchableOpacity
            activeOpacity={1}
            onPress={() => {
              scrollToIndex(activeIndex + 1);
            }}
          >
            <ChevronRightSVG
              color={currentImg?.chevronRigthColor || colors.light[100]}
              width={15}
              height={31}
            />
          </TouchableOpacity>
        )}
      </View>
    );
  }, [activeIndex, images]);

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<ImgProps>) => {
      return (
        <>
          <TouchableOpacity
            className="sm:hidden"
            activeOpacity={1}
            onPress={item.onPress}
          >
            <ImageBackground
              source={
                typeof item.src.mobile === 'string'
                  ? { uri: item.src.mobile }
                  : item.src.mobile
              }
              style={{
                width: bannerWidth,
                height: bannerWidth,
              }}
              className={`items-center justify-center px-2`}
              onLoadStart={() => {
                if (Platform.OS !== 'web') {
                  setLoading(true);
                }
              }}
              onLoadEnd={() => setLoading(false)}
            >
              <Chevrons />

              {loading && (
                <ActivityIndicator size={'large'} color={colors.primary[600]} />
              )}
            </ImageBackground>
          </TouchableOpacity>

          <TouchableOpacity
            className="hidden sm:flex"
            activeOpacity={1}
            onPress={item.onPress}
          >
            <ImageBackground
              source={
                typeof item.src.web === 'string'
                  ? { uri: item.src.web }
                  : item.src.web
              }
              style={{
                width: bannerWidth,
                height: bannerWidth / 3.44,
              }}
              className={`items-center justify-center px-2`}
            >
              <Chevrons />
            </ImageBackground>
          </TouchableOpacity>
        </>
      );
    },
    [loading, bannerWidth, Chevrons]
  );

  useFocusEffect(
    useCallback(() => {
      const interval = setInterval(() => {
        if (activeIndex === images.length - 1) {
          scrollToIndex(0);
        } else {
          scrollToIndex(activeIndex + 1);
        }
      }, 5000);
      return () => {
        clearInterval(interval);
      };
    }, [activeIndex, images.length])
  );

  return (
    <>
      <FlatList
        ref={flatListRef}
        data={images}
        horizontal
        pagingEnabled
        getItemLayout={(__data, index: number) => ({
          length: bannerWidth,
          offset: bannerWidth * index,
          index,
        })}
        onLayout={(e) => setBannerWidth(e.nativeEvent.layout.width)}
        showsHorizontalScrollIndicator={false}
        renderItem={renderItem}
        viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs.current}
        keyExtractor={(_, index) => index.toString()}
      />

      {images.length > 1 && (
        <View className="flex-row items-center justify-center mt-4 hidden sm:flex">
          {images.map((_, index) => (
            <TouchableOpacity
              onPress={() => {
                scrollToIndex(index);
                setActiveIndex(index);
              }}
              key={index.toString()}
              className={twMerge(
                'w-[10px] h-[10px] rounded-full mr-2',
                index === activeIndex ? `bg-primary-600` : `bg-gray-300`
              )}
            />
          ))}
        </View>
      )}
    </>
  );
};

export default ImageCarousel;
